// Shared font loader for text fields in looks. Used by the Look Editor
// canvas, look preview thumbnails, and the Batch Test popup so all three
// share one in-memory registry — a font fetched once is available
// everywhere.

const loadedFonts = new Set();

const familyNameFor = (fontFilename) => (
  fontFilename ? fontFilename.replace(/\.[^.]+$/, '') : null
);

export const loadFont = async (fontFilename, fontS3Path) => {
  if (!fontFilename || loadedFonts.has(fontFilename)) return;
  loadedFonts.add(fontFilename); // mark attempted immediately to dedupe in-flight fetches
  try {
    // The trailing-slash normalizer must run on the resolved base path, not the
    // raw prop — an empty/null fontS3Path used to produce a double slash like
    // s3://shoobphoto/fonts//font.otf which S3 treats as a distinct (missing) key.
    const basePath = fontS3Path || 's3://shoobphoto/fonts/';
    const s3Key = basePath + (basePath.endsWith('/') ? '' : '/') + fontFilename;
    const { fetchPresignedUrl } = await import('./api');
    const url = await fetchPresignedUrl(s3Key.replace(/^s3:\/\/[^/]+\//, ''));
    const face = new FontFace(familyNameFor(fontFilename), `url(${url})`);
    const loaded = await face.load();
    document.fonts.add(loaded);
  } catch (err) {
    loadedFonts.delete(fontFilename); // allow retry
  }
};

// For canvas rendering: only apply the custom family once the font is
// registered — otherwise the canvas silently draws with fallback and we
// can't tell why. DOM elements should use `cssFontFamily` instead so the
// browser handles the fallback swap automatically.
export const getCanvasFontFamily = (fontFilename) => {
  if (!fontFilename) return 'sans-serif';
  return loadedFonts.has(fontFilename)
    ? `"${familyNameFor(fontFilename)}", sans-serif`
    : 'sans-serif';
};

// For DOM elements: always emit the custom family with a sans-serif
// fallback. The browser will re-render text automatically when the font
// finishes loading — no manual re-render tick needed.
export const cssFontFamily = (fontFilename) => (
  fontFilename ? `"${familyNameFor(fontFilename)}", sans-serif` : 'sans-serif'
);

export const isFontLoaded = (fontFilename) => (
  !!fontFilename && loadedFonts.has(fontFilename)
);
